// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View, type ViewStyle } from 'react-native';
import { createTheme } from '../design-system/theme';
import { useColorScheme } from '../foundations/colorScheme';
import { callHistoryStrings } from './strings';
import type { CallHistoryFilter } from './types';

export const CALL_HISTORY_FILTERS: readonly CallHistoryFilter[] = ['all', 'missed', 'favorites'];

export interface CallHistoryFilterBarProps {
  readonly active: CallHistoryFilter;
  readonly onChange: (filter: CallHistoryFilter) => void;
}

export const CallHistoryFilterBar: React.FC<CallHistoryFilterBarProps> = ({ active, onChange }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);

  const handlePress = useCallback(
    (next: CallHistoryFilter) => () => {
      onChange(next);
    },
    [onChange],
  );

  return (
    <View
      style={[styles.bar, { borderBottomColor: theme.colors.border.secondary }]}
      accessibilityRole="tablist"
    >
      {CALL_HISTORY_FILTERS.map((filter) => {
        const isActive = filter === active;
        return (
          <Pressable
            key={filter}
            onPress={handlePress(filter)}
            accessibilityRole="tab"
            accessibilityLabel={callHistoryStrings.filter(filter)}
            accessibilityState={{ selected: isActive }}
            style={({ pressed }) => [
              styles.chip,
              {
                borderColor: isActive ? theme.colors.accent.indigo : theme.colors.border.primary,
                backgroundColor: isActive
                  ? theme.colors.accent.indigo
                  : theme.colors.surface.primary,
                opacity: pressed ? 0.8 : 1,
              },
            ]}
          >
            <Text
              style={[
                styles.chipText,
                {
                  color: isActive ? theme.colors.text.onAccent : theme.colors.text.primary,
                  fontWeight: isActive ? '600' : '500',
                },
              ]}
            >
              {callHistoryStrings.filter(filter)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
  } satisfies ViewStyle,
  chipText: {
    fontSize: 13,
  } satisfies TextStyle,
});
